import {
  POST_GAME_SURVEY_QUESTIONS,
  PRE_GAME_SURVEY_QUESTIONS
} from '@/config/empiricalStudy';
import type { EmpiricalStudyParticipantRecord } from '@/types';

const toIsoString = (value?: Date): string => {
  return value instanceof Date ? value.toISOString() : '';
};

const escapeCsvValue = (value: string | number | undefined): string => {
  const text = value === undefined ? '' : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const collectSurveyColumns = (
  prefix: 'pre' | 'post',
  records: EmpiricalStudyParticipantRecord[]
): string[] => {
  const configuredIds =
    prefix === 'pre'
      ? PRE_GAME_SURVEY_QUESTIONS.map((question) => question.id)
      : POST_GAME_SURVEY_QUESTIONS.map((question) => question.id);

  const dynamicIds = new Set<string>();
  records.forEach((record) => {
    const source =
      prefix === 'pre'
        ? record.preSurveyResponses
        : record.postSurveyResponses || {};
    Object.keys(source).forEach((key) => dynamicIds.add(key));
  });

  const merged = Array.from(new Set([...configuredIds, ...dynamicIds]));
  return merged.map((id) => `${prefix}_${id}`);
};

export const buildEmpiricalStudyCsv = (
  records: EmpiricalStudyParticipantRecord[]
): string => {
  const preSurveyColumns = collectSurveyColumns('pre', records);
  const postSurveyColumns = collectSurveyColumns('post', records);

  const headers = [
    'participant_code',
    'session_id',
    'game_code',
    'teacher_id',
    'teacher_name',
    'question_set_id',
    'question_set_name',
    'player_id',
    'player_name',
    'consent_choice',
    'joined_at',
    'pre_survey_submitted_at',
    'game_started_at',
    'game_completed_at',
    'game_duration_seconds',
    'final_score',
    'post_survey_submitted_at',
    'status',
    ...preSurveyColumns,
    ...postSurveyColumns
  ];

  const rows = records.map((record) => {
    const preSurveyData = Object.fromEntries(
      preSurveyColumns.map((columnName) => {
        const key = columnName.replace(/^pre_/, '');
        return [columnName, record.preSurveyResponses[key] || ''];
      })
    );

    const postSurveyData = Object.fromEntries(
      postSurveyColumns.map((columnName) => {
        const key = columnName.replace(/^post_/, '');
        return [columnName, record.postSurveyResponses?.[key] || ''];
      })
    );

    const baseRow: Record<string, string | number | undefined> = {
      participant_code: record.participantCode,
      session_id: record.sessionId,
      game_code: record.gameCode,
      teacher_id: record.teacherId,
      teacher_name: record.teacherName,
      question_set_id: record.questionSetId,
      question_set_name: record.questionSetName,
      player_id: record.playerId,
      player_name: record.playerName,
      consent_choice: record.consentChoice,
      joined_at: toIsoString(record.joinedAt),
      pre_survey_submitted_at: toIsoString(record.preSurveySubmittedAt),
      game_started_at: toIsoString(record.gameStartedAt),
      game_completed_at: toIsoString(record.gameCompletedAt),
      game_duration_seconds: record.gameDurationSeconds,
      final_score: record.finalScore,
      post_survey_submitted_at: toIsoString(record.postSurveySubmittedAt),
      status: record.status,
      ...preSurveyData,
      ...postSurveyData
    };

    return headers.map((header) => escapeCsvValue(baseRow[header])).join(',');
  });

  return [headers.join(','), ...rows].join('\n');
};

export const downloadEmpiricalStudyCsv = (
  csvContent: string,
  fileName: string
): void => {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
